import React, { useState } from "react";
import { toast } from "sonner";
import { KeyRound, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthProvider";
import { useT } from "@/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const inputCls = "h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-brand";

// Non-blocking prompt shown to admins that still use the default password.
export default function AdminPasswordPrompt() {
  const { user, checkAuth } = useAuth();
  const t = useT();
  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!user || !user.must_change_password || dismissed) return null;

  const submit = async () => {
    setError(null);
    if (next !== confirm) { setError(t("admin.firstLogin.mismatch")); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success(t("admin.firstLogin.success"));
      await checkAuth();
      setOpen(false);
    } catch (e) {
      setError(e?.response?.data?.detail || t("common.confirm.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed bottom-4 left-1/2 z-50 flex max-w-[92%] -translate-x-1/2 items-center gap-3 rounded-lg border border-brand/40 bg-card px-4 py-3 shadow-lg lg:left-[calc(50%+8rem)]" data-testid="admin-password-banner">
        <KeyRound className="h-4 w-4 shrink-0 text-brand" />
        <span className="text-sm text-muted-foreground">{t("admin.firstLogin.banner")}</span>
        <button onClick={() => setOpen(true)} data-testid="admin-password-open" className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90">
          {t("admin.firstLogin.changePassword")}
        </button>
        <button onClick={() => setDismissed(true)} data-testid="admin-password-dismiss" className="rounded-md p-1 text-muted-foreground hover:bg-secondary">
          <X className="h-4 w-4" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={(o) => !saving && setOpen(o)}>
        <DialogContent data-testid="admin-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{t("admin.firstLogin.title")}</DialogTitle>
            <DialogDescription>{t("admin.firstLogin.desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">{t("admin.firstLogin.current")}</label>
              <input type="password" className={inputCls} value={cur} onChange={(e) => setCur(e.target.value)} data-testid="admin-password-current" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">{t("admin.firstLogin.new")}</label>
              <input type="password" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} data-testid="admin-password-new" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">{t("admin.firstLogin.confirm")}</label>
              <input type="password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="admin-password-confirm" />
            </div>
            {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="admin-password-error">{error}</p>}
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} disabled={saving} className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-60">{t("admin.firstLogin.later")}</button>
            <button onClick={submit} disabled={saving || !cur || next.length < 6} data-testid="admin-password-submit" className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}{t("admin.firstLogin.submit")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
